export type ReAuthTransactionDetails = {
  transactionId: string;
  customerId: string;
  clientSecret: string;
  publishableKey: string;
  amount: number;
  feeAmount: number;
  totalAmount: number;
  status: string | null;
};

type RecordValue = Record<string, unknown>;

const asRecord = (value: unknown): RecordValue | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as RecordValue)
    : null;

const firstString = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
};

const moneyValue = (value: unknown): number | null => {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && !value.trim()) return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0
    ? numericValue
    : null;
};

/** Accepts the transaction-details API envelope while keeping Stripe's
 * credentials in component memory only. */
export const parseReAuthTransactionDetails = (
  response: unknown
): ReAuthTransactionDetails | null => {
  const root = asRecord(response);
  const body = asRecord(root?.body) || root;
  if (!body || body.success === false) return null;

  const data = asRecord(body.data) || body;
  const order = asRecord(data.orderDetails);
  const paymentMethod = asRecord(data.paymentMethodDetails);
  const cardGateway = asRecord(paymentMethod?.cc_gateway_name);

  const transactionId = firstString(
    order?.transaction_id,
    order?.transactionId
  );
  const customerId = firstString(
    order?.cc_ref_number,
    order?.customer_id,
    order?.customerId
  );
  const clientSecret = firstString(
    order?.client_secret,
    order?.clientSecret,
    paymentMethod?.client_secret,
    paymentMethod?.clientSecret,
    data.client_secret,
    data.clientSecret
  );
  const publishableKey = firstString(
    paymentMethod?.gateway_key,
    paymentMethod?.stripe_public_key,
    paymentMethod?.stripe_publishable_key,
    paymentMethod?.stripe_publickey,
    paymentMethod?.publishable_key,
    paymentMethod?.public_key,
    paymentMethod?.stripePublicKey,
    paymentMethod?.publishableKey,
    cardGateway?.key,
    paymentMethod?.key
  );
  const amount = moneyValue(order?.amount);
  const feeAmount = moneyValue(order?.cc_fee_amount);
  const totalAmount = moneyValue(order?.total_amount);

  if (
    !transactionId?.startsWith("pi_") ||
    !customerId?.startsWith("cus_") ||
    !clientSecret?.startsWith(`${transactionId}_secret_`) ||
    !publishableKey?.startsWith("pk_") ||
    amount === null ||
    feeAmount === null ||
    totalAmount === null ||
    totalAmount <= 0 ||
    Math.abs(Math.round((amount + feeAmount) * 100) - Math.round(totalAmount * 100)) > 1
  ) {
    return null;
  }

  return {
    transactionId,
    customerId,
    clientSecret,
    publishableKey,
    amount,
    feeAmount,
    totalAmount,
    status: firstString(order?.status, data.status),
  };
};
